import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { TIERS, THEMED } from "@/lib/badges";
import { VALUES } from "@/lib/constants";
import { Award, ArrowRight, BadgeCheck, Medal, Search, Clock, ShieldCheck, Sparkles } from "lucide-react";

const TIER_META = {
  bronze: { tag: "Starter", desc: "Your first milestone. Proof you showed up.", color: "text-amber-700" },
  silver: { tag: "Committed", desc: "Real commitment employers notice.", color: "text-neutral-400" },
  gold: { tag: "Advanced", desc: "Serious impact. A standout on any CV.", color: "text-amber-500" },
  platinum: { tag: "Leader", desc: "The ultimate achievement. A community leader.", color: "text-forest" },
};

const STEPS = [
  { icon: Search, t: "Find opportunities", d: "Browse and apply for roles that match your passions." },
  { icon: Clock, t: "Complete hours", d: "Volunteer your time and log it with a short reflection." },
  { icon: ShieldCheck, t: "Get verified", d: "Organisations approve your hours, so recognition carries real weight." },
  { icon: Award, t: "Earn recognition", d: "Unlock certificates and badges for your CV and LinkedIn." },
];

export default function Awards() {
  const { user } = useAuth();
  const [causes, setCauses] = useState({});
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.causeBadges().then(setCauses).catch(() => {});
    if (user && user.user_type !== "organisation") {
      api.myAwardProgress().then(setProgress).catch(() => {});
    }
  }, [user]);

  const myHours = progress?.total_hours || 0;

  return (
    <div className="bg-cream" data-testid="awards-page">
      <Seo title="Awards & recognition" description="Turn verified volunteer hours into certificates and badges for your CV, UCAS statement and LinkedIn. Every hour counts — get the proof." />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-14">
        <div className="max-w-3xl animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/80 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-forest/80">
            <Medal className="w-3.5 h-3.5" /> Recognition & growth
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter leading-[1.05] text-forest mt-7">
            Every hour counts.<br /><span className="shine-text">Get the proof.</span>
          </h1>
          <p className="text-forest/70 leading-relaxed text-lg mt-7 max-w-xl">
            Every hour you log is verified by the organisation you helped — then turned into certificates
            and badges that strengthen your CV, university applications and LinkedIn.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={user ? "/discover" : "/login"}>
              <Button data-testid="awards-start-cta" size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12 shadow-lg shadow-terracotta/20 transition-transform hover:-translate-y-0.5">
                Start earning now <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <a href="#badges">
              <Button size="lg" variant="outline" className="rounded-full border-forest/20 text-forest bg-white/70 hover:bg-white hover:text-forest px-7 h-12 transition-transform hover:-translate-y-0.5">
                Explore badges
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Certificates */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Certificate milestones</div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Your journey to recognition</h2>
          <p className="text-forest/70 mt-4 max-w-xl text-lg">Unlock verified certificates at key hour milestones. Download and share each one the moment you earn it.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {TIERS.map((t, i) => {
            const meta = TIER_META[t.id];
            const pct = Math.min(100, Math.round((myHours / t.threshold) * 100));
            const earned = myHours >= t.threshold;
            return (
              <Reveal key={t.id} delay={i * 0.08}>
                <div data-testid={`award-tier-${t.id}`} className={`bg-white border rounded-3xl p-7 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5 ${earned ? "border-forest/40" : "border-forest/10"}`}>
                  <div className="flex items-start justify-between">
                    <Award className={`w-10 h-10 ${meta.color}`} />
                    <Badge className={`rounded-full border-none text-[11px] ${earned ? "bg-forest text-white" : "bg-neutral-100 text-neutral-500"}`}>
                      {earned ? "Earned" : meta.tag}
                    </Badge>
                  </div>
                  <div className="font-display text-xl text-forest mt-4">{t.label} Certificate</div>
                  <div className="text-xs text-neutral-500 mt-1">{t.threshold} verified hours</div>
                  <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{meta.desc}</p>
                  <div className="mt-5">
                    <div className="flex justify-between text-[11px] text-neutral-500 mb-1.5">
                      <span>Progress</span><span>{Math.min(myHours, t.threshold)}/{t.threshold} hours</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        {!user && (
          <Reveal>
            <div className="mt-6 text-sm text-neutral-500">
              <Link to="/login" className="text-forest font-medium hover:underline">Sign in</Link> to see your live progress on every certificate.
            </div>
          </Reveal>
        )}
      </section>

      {/* Cause badges */}
      <section id="badges" className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Specialised recognition</div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Cause badges</h2>
          <p className="text-forest/70 mt-4 max-w-xl text-lg">Beyond hours, earn badges for the causes you champion. Each cause has 10, 50 and 100-hour milestones.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {VALUES.map((v, i) => {
            const c = causes[v.id] || {};
            const mine = progress?.causes?.[v.id] || 0;
            return (
              <Reveal key={v.id} delay={(i % 3) * 0.08}>
                <div data-testid={`cause-badge-${v.id}`} className="bg-white border border-forest/10 rounded-3xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-xl text-forest">{v.label}</div>
                    <Sparkles className="w-5 h-5 text-terracotta/60" />
                  </div>
                  <div className="flex gap-2 mt-4">
                    {[10, 50, 100].map(m => (
                      <Badge key={m} className={`rounded-full border-none text-[11px] ${mine >= m ? "bg-forest text-white" : "bg-neutral-100 text-neutral-500"}`}>
                        {m}h{c[`issued_${m}`] > 0 && <span className="ml-1 opacity-70">· {c[`issued_${m}`]} earned</span>}
                      </Badge>
                    ))}
                  </div>
                  {mine > 0 && (
                    <div className="text-xs text-forest font-medium mt-3">You: {mine}h verified in this cause</div>
                  )}
                  {(c.earners || []).length > 0 && (
                    <div className="flex items-center gap-1.5 mt-4">
                      <div className="flex -space-x-2">
                        {c.earners.map((e, j) => (
                          <Avatar key={j} className="w-7 h-7 border-2 border-white">
                            <AvatarImage src={imgSrc(e.picture)} alt={e.name} />
                            <AvatarFallback className="bg-forest text-white text-[10px]">{e.name?.[0]}</AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                      <span className="text-[11px] text-neutral-500">earned by {c.earners.map(e => e.name?.split(" ")[0]).join(", ")}</span>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Themed badges */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <Reveal>
          <div className="civic-glass rounded-3xl p-8 sm:p-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">
              <BadgeCheck className="w-4 h-4" /> Achievement badges
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-6">
              {THEMED.map(b => (
                <div key={b.id}>
                  <div className="font-display text-forest">{b.label}</div>
                  <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">The process</div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Four steps to certified impact</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div className="bg-white border border-forest/10 rounded-3xl p-8 h-full">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-2xl bg-terracotta/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-terracotta" />
                  </div>
                  <span className="font-display text-3xl text-forest/15">{i + 1}</span>
                </div>
                <div className="font-display text-xl text-forest mt-5">{s.t}</div>
                <div className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest text-white py-20 mt-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight">Ready to start earning?</h2>
            <p className="text-white/80 mt-3 max-w-lg mx-auto">Find your first opportunity today and begin your journey toward verified recognition.</p>
            <div className="flex justify-center gap-3 mt-8 flex-wrap">
              <Link to="/opportunities"><Button className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">Find opportunities</Button></Link>
              {!user && <Link to="/login"><Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12">Create account</Button></Link>}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
